import type { IQuestionAnswer } from '@/services/values/PageAnswers'
import type { Dictionary } from '@/types'
import type Page from '@/view-models/Page'
import type Question from '@/view-models/Question'
import { defineStore } from 'pinia'
import QuestionEnum from '@/services/enums/QuestionEnum'
import PageAnswers, { QuestionAnswer } from '@/services/values/PageAnswers'
import { SubPagesQuestion } from '@/view-models/Question'

function mapSubQuestionToAnswers(q: SubPagesQuestion) {
  const pageAnswers = new Array<PageAnswers>()

  q.subPageDetails.forEach((spd, i) => {
    const subQustions = spd.page.sections.flatMap(s => s.questions)

    const pageAnswer = new PageAnswers()
    pageAnswer.title = `Item ${i + 1}`

    subQustions.forEach((sq) => {
      type extractGeneric<Type> = Type extends Question<infer X> ? X : null
      type extracted = extractGeneric<typeof sq>
      const question = sq as Question<extracted>

      const answer = new QuestionAnswer<extracted>()
      answer.id = question.id
      answer.name = question.name
      answer.label = question.label
      answer.value = question.value
      answer.visible = question.visible
      answer.display = question.display()
      answer.hideOnSummary = question.hideOnSummary()

      if (question instanceof SubPagesQuestion) {
        const mappedAnswers = mapSubQuestionToAnswers(question)
        answer.subPageAnswers = mappedAnswers
      }

      pageAnswer.answers.push(answer)
    })

    pageAnswers.push(pageAnswer)
  })

  return pageAnswers
}

function isMeaningful(value: unknown): boolean {
  return value !== null && value !== undefined && value !== ''
}

function answerValue(item: PageAnswers, name: QuestionEnum): unknown {
  const value = item.answers.find(a => a.name === name)?.value
  return isMeaningful(value) ? value : null
}

// Match a freshly mapped sub-page item to a stored one by stable identity only: PRN first, then a
// unique name, so reorders or a lost PRN cannot misattach one item's carried data to another.
// Positional pairing is left to the caller, which only applies it when the collection has not
// changed size, so a shifted collection can never carry hidden data onto the wrong row.
function findExistingItem(existing: PageAnswers[], mapped: PageAnswers, used: Set<number>): { item: PageAnswers, index: number } | undefined {
  const prn = answerValue(mapped, QuestionEnum.PRN)
  if (prn !== null) {
    const byPrn = existing.findIndex((e, ei) => !used.has(ei) && answerValue(e, QuestionEnum.PRN) === prn)
    if (byPrn !== -1)
      return { item: existing[byPrn], index: byPrn }
  }

  const name = answerValue(mapped, QuestionEnum.name)
  if (name !== null) {
    const named = existing
      .map((item, ei) => ({ item, ei }))
      .filter(({ item, ei }) => !used.has(ei) && answerValue(item, QuestionEnum.name) === name)
    if (named.length === 1)
      return { item: named[0].item, index: named[0].ei }
  }

  return undefined
}

// Merge a stored sub-page item with its freshly mapped counterpart: keep fresh visible values so a
// user can clear a field, but never let a fresh empty value clobber a populated stored hidden/system
// answer, and carry forward any stored answer whose question the current page does not manage (e.g.
// PRN, isDeceased, or fields captured on a fuller page than the one now being submitted such as an
// aggregator landing page). Nested sub-page collections are reconciled recursively so nested data survives too.
function mergeSubPageItem(existingItem: PageAnswers, mapped: PageAnswers): PageAnswers {
  const merged = new PageAnswers(mapped.title, [])
  const existingByName = new Map(existingItem.answers.map(a => [a.name, a]))
  const mappedNames = new Set(mapped.answers.map(a => a.name))

  mapped.answers.forEach((mappedAnswer) => {
    const previous = existingByName.get(mappedAnswer.name)
    const answer = previous && mappedAnswer.visible === false && !isMeaningful(mappedAnswer.value) && isMeaningful(previous.value)
      ? Object.assign(new QuestionAnswer(), mappedAnswer, { value: previous.value })
      : mappedAnswer
    // Optional chaining guards stored answers that were persisted without a subPageAnswers array.
    if ((previous?.subPageAnswers?.length ?? 0) > 0 && (mappedAnswer.subPageAnswers?.length ?? 0) > 0)
      answer.subPageAnswers = reconcileSubPageAnswers(previous?.subPageAnswers, mappedAnswer.subPageAnswers)
    merged.answers.push(answer)
  })

  // Preserve any stored answer the current page's question set does not manage: a page can only own
  // and clear the fields it actually contains, so anything absent from the fresh map must survive.
  existingItem.answers
    .filter(a => !mappedNames.has(a.name))
    // Clone so old and new answer arrays do not share object references
    .forEach(a => merged.answers.push(Object.assign(new QuestionAnswer(), a)))

  return merged
}

// Reconcile stored sub-page entries with the freshly mapped set. Items with a stable identity (PRN,
// then unique name) are matched first, so an add, remove or reorder preserves their carried hidden
// data. Remaining identity-less items are then paired by position, but only when the collection has
// not changed size, letting collections whose identity lives only in stored answers (e.g. additional
// drivers) keep that data on a plain in-place update. Positional pairing assumes the order is
// unchanged, so a reorder of identity-less items is not preserved safely; a size change disables it
// entirely so hidden data is never carried onto a shifted row after an add or remove.
function reconcileSubPageAnswers(existing: PageAnswers[] | undefined, mapped: PageAnswers[]): PageAnswers[] {
  const existingItems = existing ?? []
  const used = new Set<number>()
  const matches = new Map<number, PageAnswers>()

  mapped.forEach((item, i) => {
    const match = findExistingItem(existingItems, item, used)
    if (match) {
      used.add(match.index)
      matches.set(i, match.item)
    }
  })

  if (existingItems.length === mapped.length) {
    mapped.forEach((_item, i) => {
      if (!matches.has(i) && !used.has(i)) {
        used.add(i)
        matches.set(i, existingItems[i])
      }
    })
  }

  return mapped.map((item, i) => {
    const match = matches.get(i)
    return match ? mergeSubPageItem(match, item) : item
  })
}

interface State {
  pagesStore: Dictionary<PageAnswers>
  selectedSchemeQuoteResult: string
}

const useAnswersStore = defineStore('answers', {
  state: (): State => ({
    pagesStore: {},
    selectedSchemeQuoteResult: '',
  }),
  getters: {
    answers(state): IQuestionAnswer[] {
      return Object.keys(state.pagesStore).flatMap(p => state.pagesStore[p].answers)
    },
    pages(state): PageAnswers[] {
      return Object.values(state.pagesStore)
    },
  },
  actions: {
    setAnswers(forPage: Page): void {
      const questions = forPage.sections.flatMap(s => s.questions)
      const newPS: Dictionary<PageAnswers> = {}
      newPS[forPage.id] = new PageAnswers(forPage.title, questions.map(q => q.toAnswer()))
      this.pagesStore = { ...this.$state.pagesStore, ...newPS }
    },
    updateAnswersFromPage(page: Page): void {
      const newPS: Dictionary<PageAnswers> = this.$state.pagesStore

      const questions = page.sections.flatMap(s => s.questions)
      const currentAnswers = Object.keys(newPS).flatMap(p => newPS[p].answers)

      questions.forEach((q) => {
        const matchedAnswers = currentAnswers.filter(a => a.name === q.name)
        matchedAnswers.forEach((ma) => {
          type extractGeneric<Type> = Type extends Question<infer X> ? X : null
          type extracted = extractGeneric<typeof q>
          const typedAnswer = ma as QuestionAnswer<extracted>
          const question = q as Question<extracted>
          typedAnswer.value = question.value

          if (q instanceof SubPagesQuestion) {
            const subQuestion = q as SubPagesQuestion
            const mappedAnswers = mapSubQuestionToAnswers(subQuestion)
            typedAnswer.subPageAnswers = reconcileSubPageAnswers(typedAnswer.subPageAnswers, mappedAnswers)
          }
        })
      })

      this.pagesStore = newPS
    },
    setPageAnswers(pageAnswers: Dictionary<PageAnswers>): void {
      this.pagesStore = pageAnswers
    },
    clearAll(): void {
      this.pagesStore = {}
      this.selectedSchemeQuoteResult = ''
    },
    setSelectedSchemeQuoteResult(schemeName: string): void {
      this.selectedSchemeQuoteResult = schemeName
    },
    clearSelectedSchemeQuoteResult(): void {
      this.selectedSchemeQuoteResult = ''
    },
  },
})

export default useAnswersStore
